import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";

import _ from "lodash";
import cx from "classnames";

import styles from "./create.module.scss";
import { Form, Grid, Modal } from "semantic-ui-react";

import { generateReport } from "actions/generateReport";
import SaveChanges from "components/ui/Buttons/SaveButton";
import Cancel from "components/ui/Buttons/CancelButton";

const initReport = {
  accountId: null,
  toEmail: null,
  fromDate: null,
  toDate: null,
  type: null
};

const initErrorFields = [
  {
    name: "toEmail",
    title: "Recepient Email",
    error: false,
    message: null,
  },
  {
    name: "type",
    title: "Type of report",
    error: false,
    message: null,
  },
  {
    name: "dateRange",
    title: "Date Range",
    error: false,
    message: null,
  }
];

const reportOptions = [
  { key: 0, text: "User Login Report", value: "USER_LOGIN_REPORT" },
  { key: 2, text: "Conversation Report", value: "CONVERSATION_REPORT" },
  { key: 1, text: "Master Asset Report", value: "MASTER_ASSET_REPORT" },
];

export default function GenerateReport({ account, open, setOpen }) {
  const dispatch = useDispatch();

  const [report, setReport] = useState(initReport);
  const [error, setError] = useState(null);
  const [errorFields, setErrorFields] = useState(initErrorFields);

  const generatingReport = useSelector((state) =>
    _.get(state, "generateReports.generatingReport")
  );

  useEffect(() => {
    setReport({
      ...initReport,
      accountId: account?.id,
    });
    setErrorFields([...initErrorFields]);
    setError(null);
  }, [open, account]);

  useEffect(() => {
    if (generatingReport && !generatingReport.creating) {
      if (generatingReport.success) {
        setOpen(false);
        toast.success(`Started generating report for ${account?.name}`);
      } else if (generatingReport.error) {
        setError(generatingReport.error);
      }
    }
  }, [generatingReport]);

  const isFieldError = (fieldName) => {
    return errorFields.some((field) => field.name === fieldName && field.error);
  };

  const validateEmail = (email) => {
    const mailFormat = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (email.match(mailFormat)) {
      return true;
    }
    return false;
  };

  const validateFields = () => {
    const updatedFields = errorFields.map((field) => {
      const value = report[field.name];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return {
          ...field,
          error: true,
          message: `${field.title} is required`,
        };
      }

      if (field.name === "toEmail") {
        const isValidEmail = validateEmail(value.trim());
        if (!isValidEmail) {
          return {
            ...field,
            error: true,
            message: "Invalid Recipient Email",
          };
        }
      }

      return {
        ...field,
        error: false,
        message: null,
      };
    });

    const hasInvalidDateRange = (!report.fromDate && report.toDate) || (report.fromDate && !report.toDate);
    const finalFields = updatedFields.map((field) =>
      field.name === "dateRange"
        ? {
          ...field,
          error: hasInvalidDateRange,
          message: hasInvalidDateRange
            ? 'Both "From Date" and "To Date" must be provided, or neither.'
            : null,
        }
        : field
    );

    setErrorFields(finalFields);
    return finalFields;
  };

  const onGenerateReport = (e) => {
    e.preventDefault();
    const validationResult = validateFields();
    const hasError = validationResult.some((field) => field.error);

    if (hasError) {
      const firstError = validationResult.find((field) => field.error);
      setError(firstError.message);
      
      return;
    }

    dispatch(generateReport({ report }));
    setError(null);
  };

  return (
    <>
      <Modal
        size="tiny"
        open={open}
        centered={false}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        className={cx(styles.create)}
      >
        <Modal.Content className={cx(styles.content)}>
          <Form onSubmit={onGenerateReport}>
            <Grid>
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <span className={cx(styles.title)}> Generate Report </span>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="toEmail"
                    label={"Recepient Email"}
                    placeholder="Email To"
                    value={report.toEmail}
                    onChange={(e) => setReport({ ...report, toEmail: e.target.value })}
                    error={isFieldError("toEmail")}
                  />

                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={2}>
                <Grid.Column width={8}>
                  <Form.Field>
                    <label className={cx(styles.contractTerms)}>
                      From Date <span className={cx(styles.optional)}>(optional)</span>
                    </label>
                    <DatePicker
                      selected={report.fromDate ? new Date(report.fromDate) : null}
                      onChange={(date) =>
                        setReport({ ...report, fromDate: date ? date.toISOString() : null })
                      }
                      placeholderText="dd-mm-yyyy"
                      className={styles.randomDate}
                      showYearDropdown={true}
                      dateFormat="dd-MM-yyyy"
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Form.Field>
                    <label className={cx(styles.contractTerms)}>
                      To Date <span className={cx(styles.optional)}>(optional)</span>
                    </label>
                    <DatePicker
                      selected={report.toDate ? new Date(report.toDate) : null}
                      onChange={(date) => {
                        if (date) {
                          date.setHours(23, 59, 59, 999);
                          setReport({ ...report, toDate: date.toISOString() });
                        } else {
                          setReport({ ...report, toDate: null });
                        }
                      }}
                      placeholderText="dd-mm-yyyy"
                      className={styles.randomDate}
                      showYearDropdown={true}
                      dateFormat="dd-MM-yyyy"
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Dropdown
                    id="type"
                    label="Type of report"
                    placeholder="Select Type"
                    value={report.type}
                    onChange={(e, data) => setReport({ ...report, type: data.value })}
                    selection
                    options={reportOptions}
                    error={isFieldError("type")}
                  />

                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <div className={cx(styles.error)}> {error}</div>
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <SaveChanges
                    content={generatingReport?.generating === true ? "Generating..." : "Create"}
                    type="submit"
                    disabled={generatingReport?.generating === true} />
                  <Cancel content="Cancel" onClick={() => setOpen(false)} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
}